import { Extension, type Editor } from '@tiptap/core';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    blockIndent: {
      /** Отступ абзаца/заголовка margin-left – в отличие от sinkListItem/liftListItem работает вне списков (как кнопки отступа в Quill). */
      indentBlock: (delta: 1 | -1) => ReturnType;
    };
  }
}

const INDENT_STEP_PX = 24;
const MAX_INDENT_LEVEL = 8;

/** Отступ для обычных абзацев/заголовков – Quill позволяет отступать любой блок, не только список. */
export const BlockIndent = Extension.create({
  name: 'blockIndent',
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading'],
        attributes: {
          indent: {
            default: 0,
            parseHTML: (el) => Number(el.style.marginLeft.replace('px', '')) / INDENT_STEP_PX || 0,
            renderHTML: (attrs) => (attrs.indent ? { style: `margin-left: ${attrs.indent * INDENT_STEP_PX}px` } : {}),
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      indentBlock:
        (delta: 1 | -1) =>
        ({ tr, state, dispatch }) => {
          const { selection } = state;
          let changed = false;
          state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
            if (node.type.name !== 'paragraph' && node.type.name !== 'heading') return;
            const current = (node.attrs.indent as number) ?? 0;
            const next = Math.min(MAX_INDENT_LEVEL, Math.max(0, current + delta));
            if (next === current) return;
            if (dispatch) tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
            changed = true;
          });
          return changed;
        },
    };
  },
});

/** Внутри списка отступ переносит пункт в под-список (как в Quill); вне списка – margin-left абзаца/заголовка (BlockIndent). */
export function increaseIndent(editor: Editor) {
  const chain = editor.chain().focus();
  if (editor.can().sinkListItem('listItem')) chain.sinkListItem('listItem').run();
  else chain.indentBlock(1).run();
}

export function decreaseIndent(editor: Editor) {
  const chain = editor.chain().focus();
  if (editor.can().liftListItem('listItem')) chain.liftListItem('listItem').run();
  else chain.indentBlock(-1).run();
}
